import { useEffect, useRef, useCallback } from "react";
import {
  doc,
  setDoc,
  serverTimestamp,
  increment,
  getDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";

// ──────────────────────────────────────────────────────────────────
// useEngagementTracker
// ──────────────────────────────────────────────────────────────────
// Captures a session for proposals/{slug}/sessions/{sessionId} and
// rolls a few aggregate counters into proposals/{slug}/aggregate.
//
// Design choices:
//  - We batch on visibilitychange + pagehide. Chatty per-event writes
//    cost money and add noise; a single write per session is enough
//    to know what we need to know.
//  - sessionId lives in localStorage with a 30-min refresh window.
//    Same browser within 30 min = same session. Beyond that = new session.
//  - revisitCount is the *high-intent* signal. Anything >= 2 means
//    Sachin should reach out within 24h.
// ──────────────────────────────────────────────────────────────────

const SESSION_KEY = "sn_proposal_session";
const SESSION_REFRESH_MS = 30 * 60 * 1000; // 30 minutes
const REVISIT_KEY_PREFIX = "sn_proposal_revisit_";

function newSessionId() {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function getOrCreateSessionId() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const now = Date.now();
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id && parsed?.lastSeen && now - parsed.lastSeen < SESSION_REFRESH_MS) {
        // refresh lastSeen; same session
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ id: parsed.id, lastSeen: now })
        );
        return { id: parsed.id, isNew: false };
      }
    }
    const id = newSessionId();
    localStorage.setItem(SESSION_KEY, JSON.stringify({ id, lastSeen: now }));
    return { id, isNew: true };
  } catch {
    // localStorage unavailable (incognito, etc.) — generate ephemeral
    return { id: newSessionId(), isNew: true };
  }
}

function bumpRevisitCount(slug) {
  try {
    const k = REVISIT_KEY_PREFIX + slug;
    const current = parseInt(localStorage.getItem(k) || "0", 10);
    const next = current + 1;
    localStorage.setItem(k, String(next));
    return next;
  } catch {
    return 1;
  }
}

export default function useEngagementTracker(slug) {
  const sessionRef = useRef(null);
  const stateRef = useRef({
    startedAt: Date.now(),
    maxScrollPct: 0,
    sectionsViewed: new Set(),
    tierHovers: { thrush: 0, flycatcher: 0, hornbill: 0 },
    tierTime: { thrush: 0, flycatcher: 0, hornbill: 0 },
    activeTier: null,
    activeTierSince: null,
    ctaClicks: [],
    pdfExported: false,
    intentSignal: null,
    revisitCount: 1
  });
  const writtenRef = useRef(false);

  // ─── Session bootstrap ─────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    const { id, isNew } = getOrCreateSessionId();
    sessionRef.current = id;
    const revisit = bumpRevisitCount(slug);
    stateRef.current.revisitCount = revisit;

    // Best-effort: write an "opened" stub immediately so even if the
    // user closes before we batch, we know they opened.
    const stub = {
      slug,
      sessionId: id,
      isNewSession: isNew,
      revisitCount: revisit,
      startedAt: serverTimestamp(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
      viewport:
        typeof window !== "undefined"
          ? `${window.innerWidth}x${window.innerHeight}`
          : "",
      lang: typeof navigator !== "undefined" ? navigator.language : ""
    };
    setDoc(
      doc(db, "proposals", slug, "sessions", id),
      stub,
      { merge: true }
    ).catch(() => {});

    // bump aggregate counters (best-effort, no await)
    setDoc(
      doc(db, "proposals", slug, "aggregate", "counters"),
      {
        totalOpens: increment(1),
        lastOpenedAt: serverTimestamp(),
        ...(revisit >= 2 ? { totalRevisits: increment(1) } : {})
      },
      { merge: true }
    ).catch(() => {});
  }, [slug]);

  // ─── Scroll depth ──────────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / total) * 100));
      if (pct > stateRef.current.maxScrollPct) {
        stateRef.current.maxScrollPct = pct;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  // ─── Section visit observer (call from sections via ref) ───────
  const observeSection = useCallback((id) => (el) => {
    if (!el || !slug) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.4) {
            stateRef.current.sectionsViewed.add(id);
          }
        });
      },
      { threshold: [0, 0.4, 0.8] }
    );
    obs.observe(el);
  }, [slug]);

  // ─── Tier hover tracking ───────────────────────────────────────
  const onTierEnter = useCallback((tierId) => {
    stateRef.current.tierHovers[tierId] =
      (stateRef.current.tierHovers[tierId] || 0) + 1;
    stateRef.current.activeTier = tierId;
    stateRef.current.activeTierSince = Date.now();
  }, []);
  const onTierLeave = useCallback((tierId) => {
    if (stateRef.current.activeTier === tierId && stateRef.current.activeTierSince) {
      const dt = Date.now() - stateRef.current.activeTierSince;
      stateRef.current.tierTime[tierId] =
        (stateRef.current.tierTime[tierId] || 0) + dt;
      stateRef.current.activeTier = null;
      stateRef.current.activeTierSince = null;
    }
  }, []);

  // ─── CTA / intent recording ────────────────────────────────────
  const recordCta = useCallback((kind, meta = {}) => {
    stateRef.current.ctaClicks.push({
      kind,
      at: Date.now() - stateRef.current.startedAt,
      ...meta
    });
  }, []);

  const recordIntent = useCallback((tierId) => {
    stateRef.current.intentSignal = {
      tier: tierId,
      at: Date.now() - stateRef.current.startedAt
    };
    // also write a high-priority record to /proposals/{slug}/intents/...
    if (slug && sessionRef.current) {
      setDoc(
        doc(
          db,
          "proposals",
          slug,
          "intents",
          `${sessionRef.current}_${Date.now()}`
        ),
        {
          slug,
          sessionId: sessionRef.current,
          tier: tierId,
          at: serverTimestamp()
        },
        { merge: true }
      ).catch(() => {});
      // bump aggregate so it's findable from the dashboard view
      setDoc(
        doc(db, "proposals", slug, "aggregate", "counters"),
        { totalIntents: increment(1), lastIntentAt: serverTimestamp() },
        { merge: true }
      ).catch(() => {});
    }
  }, [slug]);

  const recordPdfExport = useCallback(() => {
    stateRef.current.pdfExported = true;
  }, []);

  // ─── Batched write on unload / hide ────────────────────────────
  useEffect(() => {
    if (!slug) return;
    const flush = () => {
      if (writtenRef.current) return;
      const id = sessionRef.current;
      if (!id) return;
      // close out the active tier timer if user leaves while hovering
      if (stateRef.current.activeTier && stateRef.current.activeTierSince) {
        const dt = Date.now() - stateRef.current.activeTierSince;
        stateRef.current.tierTime[stateRef.current.activeTier] =
          (stateRef.current.tierTime[stateRef.current.activeTier] || 0) + dt;
      }
      const payload = {
        endedAt: serverTimestamp(),
        durationMs: Date.now() - stateRef.current.startedAt,
        maxScrollPct: stateRef.current.maxScrollPct,
        sectionsViewed: Array.from(stateRef.current.sectionsViewed),
        tierHovers: stateRef.current.tierHovers,
        tierTimeMs: stateRef.current.tierTime,
        ctaClicks: stateRef.current.ctaClicks,
        pdfExported: stateRef.current.pdfExported,
        intentSignal: stateRef.current.intentSignal,
        revisitCount: stateRef.current.revisitCount
      };
      writtenRef.current = true;
      setDoc(doc(db, "proposals", slug, "sessions", id), payload, {
        merge: true
      }).catch(() => {});
    };

    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [slug]);

  return {
    observeSection,
    onTierEnter,
    onTierLeave,
    recordCta,
    recordIntent,
    recordPdfExport
  };
}

// Helper to read the proposal config doc from Firestore (optional).
// For Nature's Nest we keep config in code (faster iteration). This exists
// for when you want to hot-edit copy without redeploying.
export async function fetchRemoteProposalOverrides(slug) {
  try {
    const ref = doc(db, "proposals", slug);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data();
  } catch {
    return null;
  }
}
